import { CliUserCode } from "@pagerless/schema";
import { useState } from "react";
import { Link, Navigate, useLocation } from "react-router";
import { ApiError } from "../api";
import { ScreenHead } from "../components/ScreenHead";
import { ErrorNote, Loading } from "../components/Shell";
import { useDeps, useResource } from "../context";
import { cliConnectPager, LOADING_PAGER } from "../lib/pager";
import { PasskeyCancelled } from "../lib/passkey";
import { TopBar } from "./Landing";

type Outcome = "approved" | "denied" | null;

/**
 * `npx pagerless connect` opens this page with its code. The owner checks the code matches the
 * terminal and allows it with their passkey. The terminal then gets a one-hour, connect-only token.
 */
export function Connect() {
  const location = useLocation();
  const raw = new URLSearchParams(location.search).get("code") ?? "";
  const parsed = CliUserCode.safeParse(raw);
  const code = parsed.success ? `${parsed.data.slice(0, 4)}-${parsed.data.slice(4)}` : null;
  return (
    <>
      <title>Connect your code · Pagerless</title>
      <TopBar minimal />
      <main id="main" className="center">
        <div className="win auth" style={{ maxWidth: 520 }}>
          {code ? (
            <Approve code={code} />
          ) : (
            <>
              <ScreenHead pager={cliConnectPager({ code: null, state: "expired" })} title="No code here" />
              <p className="muted">
                Run <code>npx pagerless connect</code> in your repo. It opens this page with a code.
              </p>
            </>
          )}
        </div>
      </main>
    </>
  );
}

function Approve({ code }: { code: string }) {
  const { api, passkey } = useDeps();
  const location = useLocation();
  const grant = useResource((a) => a.cliGrant(code), code);
  const [busy, setBusy] = useState<"allow" | "deny" | "passkey" | null>(null);
  const [outcome, setOutcome] = useState<Outcome>(null);
  const [error, setError] = useState<Error | null>(null);
  const [needsPasskey, setNeedsPasskey] = useState(false);

  if (grant.status === "error" && grant.error instanceof ApiError && grant.error.status === 401) {
    return <Navigate to="/login" replace state={{ from: `${location.pathname}${location.search}` }} />;
  }
  if (grant.status === "loading") {
    return (
      <>
        <ScreenHead pager={LOADING_PAGER} title="Connect your code" />
        <Loading what="Checking the code" />
      </>
    );
  }
  if (grant.status === "error") {
    const expired = grant.error instanceof ApiError && grant.error.status === 404;
    return (
      <>
        <ScreenHead
          pager={cliConnectPager({ code, state: expired ? "expired" : "error" })}
          title={expired ? "This code has expired" : "Couldn't check this code"}
        />
        {expired ? (
          <p className="muted">
            Run <code>npx pagerless connect</code> again for a new one.
          </p>
        ) : (
          <ErrorNote error={grant.error} retry={grant.reload} />
        )}
      </>
    );
  }

  const g = grant.data;
  const state = outcome ?? g.state;

  async function allow() {
    setBusy("allow");
    setError(null);
    try {
      const options = await api.cliOptions(code);
      const response = await passkey.login(options);
      await api.cliApprove(code, response);
      setOutcome("approved");
    } catch (e) {
      if (e instanceof ApiError && e.code === "passkey_required") setNeedsPasskey(true);
      else setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(null);
    }
  }

  async function addPasskey() {
    setBusy("passkey");
    setError(null);
    try {
      const options = await api.passkeyRegisterOptions();
      await api.passkeyRegisterVerify(await passkey.register(options));
      setNeedsPasskey(false);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(null);
    }
  }

  async function deny() {
    setBusy("deny");
    setError(null);
    try {
      await api.cliDeny(code);
      setOutcome("denied");
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setBusy(null);
    }
  }

  const who = g.host ? `on ${g.host}` : "in a terminal";
  const what = g.repo ? g.repo : "your code";

  if (state === "approved" || state === "used") {
    return (
      <>
        <ScreenHead pager={cliConnectPager({ code, state: "approved" })} title="Allowed" live />
        <p className="muted">Go back to your terminal. It shows the plan and asks before changing anything.</p>
        <p className="note">
          <Link to="/setup">Watch your setup screen</Link> light up as your services check in.
        </p>
      </>
    );
  }
  if (state === "denied") {
    return (
      <>
        <ScreenHead pager={cliConnectPager({ code, state: "denied" })} title="Not allowed" live />
        <p className="muted">Nothing was connected. The terminal stops.</p>
      </>
    );
  }

  return (
    <>
      <ScreenHead pager={cliConnectPager({ code, state: "pending" })} title={`Connect ${what}?`} live />
      <p>
        The Pagerless command {who} wants to connect <b>{what}</b> to your account.
      </p>
      <p className="muted">
        Only allow it if your terminal shows <b style={{ fontFamily: "var(--mono)" }}>{g.userCode}</b>.
      </p>
      <p className="note">
        For one hour it can add apps and their keys and connect hosts. It can't see problems, people or settings.
      </p>
      {needsPasskey ? (
        <>
          <p role="alert">Add a passkey first. Connecting code needs Face ID or your fingerprint.</p>
          <div className="btn-row">
            <button type="button" className="btn" onClick={addPasskey} disabled={busy !== null}>
              {busy === "passkey" ? "Adding…" : "Add a passkey"}
            </button>
          </div>
        </>
      ) : (
        <div className="btn-row">
          <button type="button" className="btn" onClick={allow} disabled={busy !== null}>
            {busy === "allow" ? "Waiting for your passkey…" : "Allow with passkey"}
          </button>
          <button type="button" className="btn alt" onClick={deny} disabled={busy !== null}>
            Deny
          </button>
        </div>
      )}
      {error &&
        (error instanceof PasskeyCancelled ? <p className="note">{error.message}</p> : <ErrorNote error={error} />)}
    </>
  );
}
